import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, money, dateFmt, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tag, MapPin, CheckCircle, Sparkle, Handshake, Buildings } from "@phosphor-icons/react";

function ClaimDialog({ req, myHotels }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [hotelId, setHotelId] = useState(req.match?.hotel_id || myHotels[0]?.id || "");
  const [counter, setCounter] = useState("");

  const mut = useMutation({
    mutationFn: () => api.claimRateRequest(req.id, hotelId, counter ? Number(counter) : null),
    onSuccess: (res) => {
      qc.invalidateQueries();
      toast.success(counter ? "Claimed — your counter-offer was sent" : "Claimed — negotiation opened with the company");
      setOpen(false);
      if (res?.rfp?.id) navigate(`/negotiation/${res.rfp.id}`);
    },
    onError: (e) => toast.error(errMsg(e, "Could not claim this request")),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`claim-btn-${req.id}`}
          className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] flex items-center gap-2 whitespace-nowrap">
          <Handshake size={16} weight="bold" /> Claim & respond
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">Claim rate request</DialogTitle></DialogHeader>
        <p className="text-sm text-[#4B5563] -mt-2">
          <b>{req.company_name}</b> asked <b>{req.hotel_name}</b> for {money(req.nightly_price)}/nt.
          Respond with one of your listings — this opens a negotiation you can counter.
        </p>
        {myHotels.length === 0 ? (
          <div className="border border-dashed border-[#E5E7EB] p-4 text-sm text-[#4B5563]">
            Add a hotel listing first, then you can claim requests.
          </div>
        ) : (
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">Respond with which property?</span>
            <select data-testid={`claim-hotel-select-${req.id}`} value={hotelId} onChange={(e) => setHotelId(e.target.value)}
              className="w-full border border-[#E5E7EB] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {myHotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </label>
        )}
        {myHotels.length > 0 && (
          <label className="text-sm">
            <span className="overline text-[#4B5563] block mb-1">Your counter rate / night (optional)</span>
            <input data-testid={`claim-counter-${req.id}`} type="number" min="1" value={counter}
              onChange={(e) => setCounter(e.target.value)}
              placeholder={`They asked ${money(req.nightly_price)} — leave blank to just open the chat`}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            <span className="text-xs text-[#9CA3AF] block mt-1">If set, we'll send this as your opening offer right away.</span>
          </label>
        )}
        <DialogFooter>
          <button data-testid={`claim-confirm-${req.id}`} disabled={mut.isPending || !hotelId}
            onClick={() => mut.mutate()}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Claiming…" : "Claim & open negotiation"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestCard({ req, myHotels }) {
  return (
    <div className="rb-card p-5 flex items-start justify-between gap-4" data-testid={`gm-request-${req.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold text-lg text-[#111827]">{req.hotel_name}</span>
          {req.match ? (
            <span className="text-xs bg-[#DCFCE7] text-[#047857] px-2 py-0.5 font-semibold inline-flex items-center gap-1">
              <CheckCircle size={12} weight="fill" /> Matches {req.match.hotel_name}
              {typeof req.match.distance_miles === "number" ? ` · ${req.match.distance_miles} mi` : ""}
            </span>
          ) : (
            <span className="text-xs bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 font-semibold inline-flex items-center gap-1">
              <Sparkle size={12} weight="fill" /> Open request
            </span>
          )}
        </div>
        <div className="text-sm text-[#4B5563] flex items-center gap-1 mt-1">
          <MapPin size={14} /> {req.address || `${req.city || ""} ${req.state || ""}`.trim() || "—"}
        </div>
        <div className="text-sm text-[#111827] mt-2">
          <b>{req.company_name}</b> · {req.num_rooms} rooms · {dateFmt(req.check_in)}–{dateFmt(req.check_out)} · {req.room_nights} room-nights
        </div>
        {req.notes && <div className="text-sm text-[#4B5563] mt-1">“{req.notes}”</div>}
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono font-black text-2xl text-[#002FA7]">{money(req.nightly_price)}</div>
        <div className="text-xs text-[#4B5563] mb-3">/ night target</div>
        <ClaimDialog req={req} myHotels={myHotels} />
      </div>
    </div>
  );
}

export default function GmRateRequests() {
  const { data } = useQuery({ queryKey: ["gmRateRequests"], queryFn: api.gmRateRequests });
  const reqs = data?.requests || [];
  const myHotels = data?.my_hotels || [];
  const matched = reqs.filter((r) => r.match);
  const others = reqs.filter((r) => !r.match);

  return (
    <div className="mt-8 space-y-8" data-testid="gm-requests-panel">
      <p className="text-sm text-[#4B5563]">
        Companies naming a price for hotels not yet on RateBridge. Claim one to open a direct negotiation using your own property.
      </p>

      {reqs.length === 0 && (
        <div className="border border-dashed border-[#E5E7EB] py-16 text-center">
          <Tag size={32} weight="duotone" color="#9CA3AF" className="mx-auto" />
          <p className="text-[#4B5563] mt-3">No open rate requests right now. New ones appear here automatically.</p>
        </div>
      )}

      {matched.length > 0 && (
        <div data-testid="gm-requests-matched">
          <h3 className="font-display font-bold text-lg text-[#111827] flex items-center gap-2">
            <CheckCircle size={18} weight="duotone" color="#047857" /> Matches your properties
          </h3>
          <p className="text-sm text-[#4B5563] mb-3">These requests match one of your listings by name or are within 5 miles.</p>
          <div className="space-y-3">
            {matched.map((r) => <RequestCard key={r.id} req={r} myHotels={myHotels} />)}
          </div>
        </div>
      )}

      {others.length > 0 && (
        <div data-testid="gm-requests-all">
          <h3 className="font-display font-bold text-lg text-[#111827] flex items-center gap-2">
            <Buildings size={18} weight="duotone" color="#002FA7" /> All open requests
          </h3>
          <p className="text-sm text-[#4B5563] mb-3">Browse every open request — claim any with one of your properties.</p>
          <div className="space-y-3">
            {others.map((r) => <RequestCard key={r.id} req={r} myHotels={myHotels} />)}
          </div>
        </div>
      )}
    </div>
  );
}
